import {
    type AccountInfo,
    AuthError,
    BrowserAuthError,
    InteractionRequiredAuthError,
    type IPublicClientApplication,
    type SilentRequest,
} from "@azure/msal-browser";
import { scopesNeeded } from "./authConfig";

/**
 * Access-token acquisition for `fetch.ts`.
 *
 * `fetch.ts` no longer depends on MSAL directly. It asks whatever *token source*
 * is registered for the current surface:
 *
 *   - Main SPA        -> an MSAL-backed source (silent renewal; initial login
 *                        redirect owned by `MsalAuthenticationTemplate`, expired
 *                        session re-established via `acquireTokenRedirect`).
 *   - Widget / copilot -> either a parent-supplied-token source (the host page
 *                        mints a token and posts it in via `cts-auth-token`), or
 *                        an MSAL source that NEVER opens a popup from a background
 *                        fetch - interactive auth only ever runs from a real user
 *                        click inside the embed's own auth provider.
 *
 * A cross-origin iframe cannot reliably open `window.open` without a user gesture
 * (returns `null` -> `empty_window_error`) and cannot use MSAL's hidden-iframe
 * silent renewal (third-party-cookie blocking). So the only correct places for a
 * popup are (a) one opportunistic `prompt:"none"` attempt on mount and (b) the
 * "Sign in" button click - both owned by `WidgetAuthProvider` /
 * `CopilotAuthProvider`, not by this module.
 */

export type AcquireTokenOptions = {
    forceRefresh?: boolean;
};

export type TokenSource = (options?: AcquireTokenOptions) => Promise<string | null>;

export type MsalTokenSourceOptions = {
    /**
     * Top-level SPA only. When silent renewal fails, hand off to a redirect
     * (`acquireTokenRedirect` for a cached account, `loginRedirect` when the
     * cache is empty) instead of returning `null`. `MsalAuthenticationTemplate`
     * only redirects on mount, so without this an expired or wiped session
     * would leave the app unauthenticated until a full reload.
     */
    redirectOnInteractionRequired?: boolean;
};

interface CustomWindow extends Window {
    __DEBUG_FORCE_INTERACTION_REQUIRED?: boolean;
}

export const DEBUG_FORCE_INTERACTION_REQUIRED = (): boolean =>
    import.meta.env.VITE_ENVIRONMENT === "dev" &&
    (window as CustomWindow).__DEBUG_FORCE_INTERACTION_REQUIRED === true;

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

let registeredSource: TokenSource | null = null;

/**
 * Register the process-wide token source `fetch.ts` uses. Last writer wins; the
 * app surface (main / widget / copilot) sets this once its auth state resolves.
 */
export const setTokenSource = (source: TokenSource): void => {
    registeredSource = source;
};

/** Drop the registered source (call on unmount of the surface that set it). */
export const clearTokenSource = (): void => {
    registeredSource = null;
};

export const hasTokenSource = (): boolean => registeredSource !== null;

export const getRegisteredTokenSource = (): TokenSource | null => registeredSource;

/** Event name dispatched on `window` when silent renewal fails and a real
 *  (click-driven) sign-in is required. The widget / copilot auth providers
 *  listen for it to re-run their SSO / Sign In button flow. */
export const AUTH_REQUIRED_EVENT = "cts-auth-required";

/**
 * Acquire an access token from the registered source. Throws if no source is
 * registered - used by non-`postAPI` call sites (e.g. the widget's raw SSE
 * fetch) that still need a bearer token.
 */
export const getAuthToken = (options?: AcquireTokenOptions): Promise<string | null> => {
    if (!registeredSource) {
        return Promise.reject(new Error("No auth token source registered"));
    }
    return registeredSource(options);
};

// ---------------------------------------------------------------------------
// interaction_in_progress recovery helpers
// ---------------------------------------------------------------------------

const INTERACTION_RETRY_DELAY_MS = 500;
const INTERACTION_MAX_RETRIES = 10;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const isInteractionInProgressError = (error: unknown): boolean =>
    error instanceof BrowserAuthError &&
    (error.errorCode === "interaction_in_progress" ||
        error.message.includes("interaction_in_progress"));

/**
 * Tell the surrounding surface that silent renewal failed and a real
 * (click-driven) sign-in is required. `WidgetAuthProvider` / `CopilotAuthProvider`
 * listen for this and re-run their SSO / Sign In button flow; the host page can
 * listen for the postMessage to supply a fresh token or a login hint.
 */
const signalInteractionRequired = (): void => {
    try {
        window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
        if (window.parent !== window) {
            window.parent.postMessage({ type: AUTH_REQUIRED_EVENT }, "*");
        }
    } catch {
        // dispatch/postMessage can throw in exotic sandboxes - never fatal here.
    }
};

// ---------------------------------------------------------------------------
// MSAL-backed source
// ---------------------------------------------------------------------------

/** sessionStorage key holding the time of the last redirect-based renewal. */
export const AUTH_REDIRECT_AT_KEY = "cts-auth-redirect-at";
/** Never bounce through AAD more than once within this window (loop guard). */
const AUTH_REDIRECT_MIN_INTERVAL_MS = 60_000;

const isTopLevelWindow = (): boolean => {
    try {
        return window.self === window.top;
    } catch {
        return false;
    }
};

const redirectedRecently = (): boolean => {
    try {
        const at = Number(sessionStorage.getItem(AUTH_REDIRECT_AT_KEY));
        return Number.isFinite(at) && at > 0 && Date.now() - at < AUTH_REDIRECT_MIN_INTERVAL_MS;
    } catch {
        return false;
    }
};

const markRedirected = (): void => {
    try {
        sessionStorage.setItem(AUTH_REDIRECT_AT_KEY, String(Date.now()));
    } catch {
        // sessionStorage unavailable - the guard just becomes best-effort.
    }
};

/**
 * Interactive fallback. In an iframe this NEVER opens a popup - a background
 * fetch has no user activation, so the popup would be blocked and yield a null
 * token that 401-loops. It signals the auth provider instead and returns null.
 *
 * In the top-level SPA (opt-in via `redirectOnInteractionRequired`) it hands
 * off to a redirect so the session is re-established instead of every request
 * going out unauthenticated:
 *
 *   - account cached  -> `acquireTokenRedirect` (expired / non-renewable
 *                        session, e.g. hidden-iframe renewal timing out behind
 *                        third-party-cookie blocking).
 *   - no account      -> `loginRedirect`. `MsalAuthenticationTemplate` only
 *                        runs its login on mount, so if the MSAL cache is wiped
 *                        mid-session (storage cleared, another tab signed out)
 *                        nobody else would ever send the user back to AAD.
 *
 * `redirectedRecently` keeps this from bouncing through AAD in a loop.
 */
const acquireTokenInteractive = async (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    options: MsalTokenSourceOptions,
): Promise<string | null> => {
    signalInteractionRequired();

    if (!options.redirectOnInteractionRequired || !isTopLevelWindow() || redirectedRecently()) {
        return null;
    }

    markRedirected();
    try {
        // Resolves once navigation to AAD has started; the token lands in the
        // cache after `handleRedirectPromise` on the way back.
        if (account) {
            await instance.acquireTokenRedirect({ scopes: scopesNeeded, account });
        } else {
            await instance.loginRedirect({ scopes: scopesNeeded });
        }
    } catch (redirectError) {
        if (!isInteractionInProgressError(redirectError)) {
            console.error("Error acquiring token via redirect:", redirectError);
        }
    }
    return null;
};

const acquireMsalToken = async (
    instance: IPublicClientApplication,
    forceRefresh: boolean,
    options: MsalTokenSourceOptions,
): Promise<string | null> => {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0] || null;

    // Use proper scopes to enable MSAL caching - empty scopes bypass the cache
    const trySilent = async (): Promise<string> => {
        if (DEBUG_FORCE_INTERACTION_REQUIRED()) {
            throw new InteractionRequiredAuthError();
        }

        const silentRequest: SilentRequest = {
            scopes: scopesNeeded, // Required scopes for proper caching
            forceRefresh,
            ...(account && { account }), // Include account if available to use cached tokens
        };

        const tokenResponse = await instance.acquireTokenSilent(silentRequest);
        if (!tokenResponse.accessToken) {
            throw new Error("No token returned from MSAL");
        }
        return tokenResponse.accessToken;
    };

    try {
        return await trySilent();
    } catch (error) {
        // Another interactive flow holds MSAL's global interaction lock. Poll for
        // it to clear, retrying the silent acquisition (which then hits the cache).
        if (isInteractionInProgressError(error)) {
            for (let attempt = 0; attempt < INTERACTION_MAX_RETRIES; attempt++) {
                await wait(INTERACTION_RETRY_DELAY_MS);
                try {
                    return await trySilent();
                } catch (retryError) {
                    if (isInteractionInProgressError(retryError)) {
                        continue;
                    }
                    if (
                        retryError instanceof InteractionRequiredAuthError ||
                        retryError instanceof AuthError
                    ) {
                        return acquireTokenInteractive(instance, account, options);
                    }
                    throw retryError;
                }
            }
            // Lock never cleared within the retry window - likely stale.
            throw new Error("Authentication in progress, please wait");
        }

        if (error instanceof InteractionRequiredAuthError || error instanceof AuthError) {
            return acquireTokenInteractive(instance, account, options);
        }

        throw error;
    }
};

/**
 * A token source backed by an MSAL instance: silent renewal with
 * interaction_in_progress recovery. Never opens a popup; the top-level SPA can
 * opt in to a redirect fallback (see `acquireTokenInteractive`).
 */
export const createMsalTokenSource =
    (instance: IPublicClientApplication, options: MsalTokenSourceOptions = {}): TokenSource =>
    ({ forceRefresh = false } = {}) =>
        acquireMsalToken(instance, forceRefresh, options);

// ---------------------------------------------------------------------------
// Parent-supplied-token source (widget / copilot embeds)
// ---------------------------------------------------------------------------

export type ParentTokenSourceConfig = {
    /** The token last handed down by the host (via `cts-auth-token`), or null. */
    getCurrentToken: () => string | null;
    /**
     * Ask the host for a fresh token and resolve with it (or null on
     * timeout / refusal). The provider owns the `cts-request-token` /
     * `cts-token-response` round-trip.
     */
    requestFreshToken: () => Promise<string | null>;
};

/**
 * A token source for an embed whose host page supplies the access token. This is
 * the fully-silent path: no popup, no third-party cookies, no MSAL in the
 * iframe. Falls back to `requestFreshToken` when there is no cached token or a
 * forced refresh is asked for.
 */
export const createParentTokenSource =
    (config: ParentTokenSourceConfig): TokenSource =>
    async ({ forceRefresh = false } = {}) => {
        if (!forceRefresh) {
            const current = config.getCurrentToken();
            if (current) {
                return current;
            }
        }
        return config.requestFreshToken();
    };
